import React from 'react';
import { sanitizeSvgFragment } from '../../utils/svgSanitize';

// Render d'un calque shape. Plusieurs sous-types :
// - shapeType === 'svg' avec svgContent (SVG complet) : pictos officiels
// - shapeType === 'svg' avec svgPath : path unique avec couleur ou gradient
// - shapeType === 'line' : trait avec caps (round/square/arrow)
// - shapeType === 'rect' / 'rounded-rect' / 'circle' : primitives SVG
//
// Les ombres (drop-shadow) sont appliquées par le wrapper data-rotated-inner
// dans LabelCanvas via getShadowStyle, pas ici.
export default function ShapeLayer({ el }) {
    // SVG path shape (from library)
    if (el.shapeType === 'svg') {
        // Multi-color SVG (official CLP pictos etc.) — render full SVG content
        if (el.svgContent) {
            return (
                <div style={{ width: '100%', height: '100%', cursor: 'move', userSelect: 'none' }}>
                    <svg
                        viewBox={el.svgViewBox || '0 0 100 100'}
                        // display:block — un svg inline s'aligne sur la BASELINE
                        // de texte et descend de ~3-4px dans son conteneur.
                        style={{ display: 'block', width: '100%', height: '100%' }}
                        // sanitizeSvgFragment : wrappe le fragment dans un
                        // <svg> root avant de sanitize, sinon DOMPurify
                        // strippe les balises SVG (cf. svgSanitize.js).
                        dangerouslySetInnerHTML={{ __html: sanitizeSvgFragment(el.svgContent) }}
                    />
                </div>
            );
        }
        // Single-color SVG path (supports solid + gradient fill)
        if (el.svgPath) {
            const isFilled = el.svgFill !== false;
            const shapeColor = el.color || '#FFFFFF';
            const hasGradient = el.fillType === 'gradient' && el.gradient?.stops;
            const gradId = `svg_grad_${el.id}`;

            // Build SVG gradient def if needed
            let gradDef = null;
            let fillValue = isFilled ? shapeColor : 'none';
            if (hasGradient && isFilled) {
                const sortedStops = [...el.gradient.stops].sort((a, b) => a.position - b.position);
                const stopsJsx = sortedStops.map((stop, i) => (
                    <stop key={i} offset={`${stop.position}%`} stopColor={stop.color} />
                ));
                if (el.gradient.type === 'radial') {
                    gradDef = (
                        <defs>
                            <radialGradient id={gradId} cx="50%" cy="50%" r="50%">
                                {stopsJsx}
                            </radialGradient>
                        </defs>
                    );
                } else {
                    const angle = (el.gradient.angle || 90);
                    const rad = (angle - 90) * Math.PI / 180;
                    const x1 = 50 - Math.cos(rad) * 50;
                    const y1 = 50 - Math.sin(rad) * 50;
                    const x2 = 50 + Math.cos(rad) * 50;
                    const y2 = 50 + Math.sin(rad) * 50;
                    gradDef = (
                        <defs>
                            <linearGradient id={gradId} x1={`${x1}%`} y1={`${y1}%`} x2={`${x2}%`} y2={`${y2}%`}>
                                {stopsJsx}
                            </linearGradient>
                        </defs>
                    );
                }
                fillValue = `url(#${gradId})`;
            }

            return (
                <div style={{ width: '100%', height: '100%', cursor: 'move', userSelect: 'none' }}>
                    <svg
                        viewBox={el.svgViewBox || '0 0 24 24'}
                        // display:block : un svg inline s'aligne sur la baseline de
                        // texte → tout le tracé descendait de ~3-4px (mortel pour les
                        // petits glyphes vectorisés importés d'un PDF).
                        // overflow visible + preserveAspectRatio none : aucun rognage
                        // d'arrondi, étirement exact dans la boîte (= rendu export).
                        style={{ display: 'block', width: '100%', height: '100%', overflow: 'visible' }}
                        preserveAspectRatio="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        {gradDef}
                        {/* Symbole NON rempli : le tracé EST le dessin, il n'existe aucun
                            contour séparé à retirer. `strokeWidth` s'applique donc toujours,
                            décoché ou non — la case « Contour » n'a pas d'état « off » à
                            offrir ici, et c'est l'inspecteur qui doit cesser de la proposer.
                            Ne pas retomber sur 1,5 quand elle est décochée : le store pose
                            systématiquement strokeWidth: 2, qui est aussi l'épaisseur de
                            dessin native de ces pictos ; forcer 1,5 amincissait de 25 % tous
                            les symboles en tracé de tous les documents déjà enregistrés, à
                            l'écran comme au PDF. */}
                        <path
                            d={el.svgPath}
                            fill={fillValue}
                            fillRule={el.svgFillRule || 'nonzero'}
                            stroke={isFilled ? (el.strokeEnabled ? (el.strokeColor || '#FFFFFF') : 'none') : shapeColor}
                            strokeWidth={isFilled ? (el.strokeEnabled ? (el.strokeWidth || 1) : 0) : (el.strokeWidth || 1.5)}
                            strokeDasharray={el.strokeDasharray}
                            strokeLinecap={el.strokeLinecap}
                        />
                    </svg>
                </div>
            );
        }
    }

    // Line / trait — rendered via SVG with marker-based arrows
    if (el.shapeType === 'line') {
        const lineColor = el.color || '#FFFFFF';
        const t = el.lineThickness !== undefined ? el.lineThickness : 0.15;
        const capL = el.lineCapLeft || 'square';
        const capR = el.lineCapRight || 'square';
        const aSize = el.lineArrowSize !== undefined ? el.lineArrowSize : 2;
        const w = el.width || 50;
        const h = el.height || 3;
        const cy = h / 2;

        const mL = `marker-L-${el.id}`;
        const mR = `marker-R-${el.id}`;

        // Line endpoints — no offset, markers handle positioning
        const x1 = 0;
        const x2 = w;

        return (
            <div style={{ width: '100%', height: '100%', cursor: 'move', overflow: 'visible' }}>
                <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ overflow: 'visible' }}>
                    <defs>
                        {capL === 'arrow' && (
                            <marker id={mL} viewBox={`0 0 ${aSize * 2} ${aSize * 2}`}
                                markerWidth={aSize * 2} markerHeight={aSize * 2}
                                refX={0} refY={aSize} orient="auto-start-reverse" markerUnits="strokeWidth">
                                <polygon points={`0,0 ${aSize * 2},${aSize} 0,${aSize * 2}`} fill={lineColor} />
                            </marker>
                        )}
                        {capR === 'arrow' && (
                            <marker id={mR} viewBox={`0 0 ${aSize * 2} ${aSize * 2}`}
                                markerWidth={aSize * 2} markerHeight={aSize * 2}
                                refX={0} refY={aSize} orient="auto" markerUnits="strokeWidth" overflow="visible">
                                <polygon points={`0,0 ${aSize * 2},${aSize} 0,${aSize * 2}`} fill={lineColor} />
                            </marker>
                        )}
                    </defs>
                    {/* Main line */}
                    <line x1={x1} y1={cy} x2={x2} y2={cy}
                        stroke={lineColor} strokeWidth={t} strokeLinecap="butt"
                        markerStart={capL === 'arrow' ? `url(#${mL})` : undefined}
                        markerEnd={capR === 'arrow' ? `url(#${mR})` : undefined}
                    />
                    {/* Round caps drawn separately so they're independent */}
                    {capL === 'round' && <circle cx={x1} cy={cy} r={t * 0.5} fill={lineColor} />}
                    {capR === 'round' && <circle cx={x2} cy={cy} r={t * 0.5} fill={lineColor} />}
                </svg>
            </div>
        );
    }

    // SVG primitive shape (rect, circle, rounded-rect) — SVG for sub-pixel stroke precision
    const strokeW = el.strokeEnabled ? (el.strokeWidth !== undefined ? el.strokeWidth : 2) : 0;
    const fillCol = el.color || '#888888';
    const radius = el.borderRadius || 0;

    // Stroke: solid or gradient
    let svgStroke = el.strokeEnabled ? (el.strokeColor || '#FFFFFF') : 'none';
    const strokeGradId = `stroke_grad_${el.id}`;
    let strokeGradDef = null;
    if (el.strokeEnabled && el.strokeType === 'gradient' && el.strokeGradient?.stops) {
        const sortedStops = [...el.strokeGradient.stops].sort((a, b) => a.position - b.position);
        const stopsJsx = sortedStops.map((stop, i) => (
            <stop key={i} offset={`${stop.position}%`} stopColor={stop.color} />
        ));
        if (el.strokeGradient.type === 'radial') {
            strokeGradDef = <radialGradient id={strokeGradId} cx="50%" cy="50%" r="50%">{stopsJsx}</radialGradient>;
        } else {
            const angle = (el.strokeGradient.angle || 90);
            const rad = (angle - 90) * Math.PI / 180;
            const x1 = 50 - Math.cos(rad) * 50, y1 = 50 - Math.sin(rad) * 50;
            const x2 = 50 + Math.cos(rad) * 50, y2 = 50 + Math.sin(rad) * 50;
            strokeGradDef = <linearGradient id={strokeGradId} x1={`${x1}%`} y1={`${y1}%`} x2={`${x2}%`} y2={`${y2}%`}>{stopsJsx}</linearGradient>;
        }
        svgStroke = `url(#${strokeGradId})`;
    }

    // Fill: solid or gradient
    let svgFill = fillCol;
    const hasShapeGradient = el.fillType === 'gradient' && el.gradient?.stops;
    const shapeGradId = `shape_grad_${el.id}`;
    let shapeGradDef = null;
    if (hasShapeGradient) {
        const sortedStops = [...el.gradient.stops].sort((a, b) => a.position - b.position);
        const stopsJsx = sortedStops.map((stop, i) => (
            <stop key={i} offset={`${stop.position}%`} stopColor={stop.color} />
        ));
        if (el.gradient.type === 'radial') {
            shapeGradDef = <radialGradient id={shapeGradId} cx="50%" cy="50%" r="50%">{stopsJsx}</radialGradient>;
        } else {
            const angle = (el.gradient.angle || 90);
            const rad = (angle - 90) * Math.PI / 180;
            const x1 = 50 - Math.cos(rad) * 50, y1 = 50 - Math.sin(rad) * 50;
            const x2 = 50 + Math.cos(rad) * 50, y2 = 50 + Math.sin(rad) * 50;
            shapeGradDef = <linearGradient id={shapeGradId} x1={`${x1}%`} y1={`${y1}%`} x2={`${x2}%`} y2={`${y2}%`}>{stopsJsx}</linearGradient>;
        }
        svgFill = `url(#${shapeGradId})`;
    }

    return (
        <div style={{ width: '100%', height: '100%', cursor: 'move', overflow: 'visible' }}>
            <svg width="100%" height="100%" style={{ overflow: 'visible' }}>
                <defs>
                    {shapeGradDef}
                    {strokeGradDef}
                </defs>
                {el.shapeType === 'circle' ? (
                    /* Rayons rentrés du demi-contour, comme le rect voisin. AVANT :
                       rx/ry valaient 50% pleins, le tracé étant centré sur le bord de la
                       boîte, sa moitié extérieure était rognée — un contour épais
                       apparaissait deux fois plus fin sur le Rond que sur le rectangle. */
                    <ellipse
                        cx="50%" cy="50%"
                        rx={`calc(50% - ${strokeW / 2}px)`} ry={`calc(50% - ${strokeW / 2}px)`}
                        fill={svgFill}
                        stroke={strokeW > 0 ? svgStroke : 'none'}
                        strokeWidth={strokeW}
                    />
                ) : (
                    <rect
                        x={strokeW / 2} y={strokeW / 2}
                        width={`calc(100% - ${strokeW}px)`} height={`calc(100% - ${strokeW}px)`}
                        rx={radius} ry={radius}
                        fill={svgFill}
                        stroke={strokeW > 0 ? svgStroke : 'none'}
                        strokeWidth={strokeW}
                    />
                )}
            </svg>
        </div>
    );
}
